import { version } from "../package.json";

import "bootswatch/dist/darkly/bootstrap.min.css";
import "./jsbeeb.css";

import { Debugger } from "./web/debug.js";
import { GamePad } from "./web/gamepads.js";
import { initialise as electron } from "./app/electron.js";
import { AudioHandler } from "./web/audio-handler.js";
import { installIcons } from "./web/icons.js";
import { QuickSettings } from "./web/quick-settings.js";
import { toast } from "./web/toast.js";
import { BuiltInImages, MediaLoader } from "./web/media-loader.js";
import { SthSource } from "./web/sth-source.js";
import { HfeSource } from "./web/hfe-source.js";
import { BitshiftersSource } from "./web/bitshifters-source.js";
import { GitHubSource } from "./web/github-source.js";
import { GoogleDriveSource } from "./web/google-drive-source.js";
import { isSnapshotFile, SnapshotUI } from "./web/snapshot-ui.js";
import { Autoboot } from "./web/autoboot.js";
import { Display } from "./web/display.js";
import { persistenceSettings } from "./web/canvas.js";
import { Layout } from "./web/layout.js";
import { EmulationLoop } from "./web/emulation-loop.js";
import { RunControls } from "./web/run-controls.js";
import { exposeConsoleSurface } from "./web/console-surface.js";
import { KeyboardSetup } from "./web/keyboard-setup.js";
import { AccessibilitySwitches } from "./web/accessibility-switches.js";
import { AnalogueInputs } from "./web/analogue-inputs.js";
import { FrontPanel } from "./web/front-panel.js";
import { Machine } from "./web/machine.js";
import { Drives } from "./web/drives.js";
import { UrlState } from "./web/url-state.js";
import { Modals } from "./web/modals.js";
import { Settings } from "./web/settings.js";
import { Config } from "./web/config.js";
import { SpeechOutput } from "./web/speech-output.js";
import { Printer } from "./printer.js";
import { DefaultStationId } from "./econet.js";
import { RewindUI } from "./web/rewind-ui.js";
import { DiscVisualiser } from "./web/disc-visualiser.js";
import { MediaWindow } from "./web/media-window.js";
import { MachineSwitch } from "./web/machine-switch.js";
import { PageActions } from "./web/page-actions.js";
import { parseMediaParams, processAutobootParams, processDriveTrackParams, processInputParams } from "./url-params.js";
import { hostKeyCodes, userKeymap } from "./keymap.js";
import { startSessionFromUrl } from "./web/shared-session.js";
import { createRendezvousClient } from "./web/rendezvous-client.js";
import { SymbolSets } from "./symbol-sets.js";

const SymbolsBaseUrl = "https://bbc.xania.org/meta/symbols/";

installIcons();

// ------------------------------------------------------------------------
// What the URL asked for.
// ------------------------------------------------------------------------

const urlState = new UrlState(window.location, window.history);
const parsedQuery = urlState.params;
let { needsAutoboot, autoType } = processAutobootParams(parsedQuery);

let discImage = BuiltInImages[0].file;
let secondDiscImage = null;
const { discImage: queryDiscImage, secondDiscImage: querySecondDisc, mmcImage } = parseMediaParams(parsedQuery);
if (queryDiscImage) discImage = queryDiscImage;
if (querySecondDisc) secondDiscImage = querySecondDisc;
const { settings: driveTracks, warnings: driveTrackWarnings } = processDriveTrackParams(parsedQuery);

const extraRoms = [];
if (Array.isArray(parsedQuery.rom)) {
    parsedQuery.rom.forEach((romPath) => {
        if (romPath) extraRoms.push(romPath);
    });
}

const cpuMultiplier = parsedQuery.cpuMultiplier ?? 1;
const noSeek = !!parsedQuery.noseek;
const stationId = parsedQuery.stationId ?? DefaultStationId;

const tryGl = parsedQuery.glEnabled ?? true;
const lowLatency = parsedQuery.lowLatency ?? true;

// ------------------------------------------------------------------------
// The pieces that exist before the machine: settings, screen, sound.
// ------------------------------------------------------------------------

const dbgr = new Debugger({ symbolSets: new SymbolSets({ baseUrl: new URL(SymbolsBaseUrl) }) });

const gamepad = new GamePad();
if (!window.isSecureContext)
    toast("Gamepads only work over https, so any joystick plugged in here is not seen.", {
        title: "Gamepads",
        quietKey: "quietInsecureGamepads",
    });

const printer = new Printer();

const accessibilitySwitches = new AccessibilitySwitches();

const settings = new Settings({ urlState });
const { keyLayout, displayMode, audioOutput, speakerAmount } = settings;
const model = settings.model;
const config = new Config(settings);

const speechOutput = new SpeechOutput();
const speak = (enabled) => {
    speechOutput.enabled = enabled;
    if (enabled && typeof speechSynthesis === "undefined")
        toast("This browser has no speech synthesis, so speech output has nothing to speak with.", { title: "Speech" });
};
speak(settings.speechOutput);
settings.on("speechOutput", speak);

// Must come after we know the model, to validate names against those of the hardware.
const keyMappingWarnings = processInputParams(parsedQuery, model.keys, hostKeyCodes, userKeymap, gamepad);
if (keyMappingWarnings.length) {
    toast(`${keyMappingWarnings.join(" ")} The key names are listed in the README.`, {
        title: "Mappings in the URL",
    });
}
if (driveTrackWarnings.length) {
    toast(`${driveTrackWarnings.join(" ")} Auto is in use instead; pick 40 or 80 on the drive in the media window.`, {
        title: "Disc drives",
    });
}

if (cpuMultiplier !== 1) console.log(`CPU multiplier set to ${cpuMultiplier}`);
const cpuSpeed = model.cyclesPerSecond;
const clocksPerSecond = (cpuMultiplier * cpuSpeed) | 0;

const screenCanvas = document.getElementById("screen");
const display = new Display({
    screenCanvas,
    model,
    mode: displayMode,
    tryGl,
    lowLatency,
    fakeVideo: parsedQuery.fakeVideo !== undefined,
    frameSkip: parsedQuery.frameSkip ?? 0,
});
const video = display.video;

const audioStatsEl = document.getElementById("audio-stats");
if (audioStatsEl) audioStatsEl.hidden = !parsedQuery.audioDebug;
const audioStatsNode = parsedQuery.audioDebug ? audioStatsEl : null;
const audioHandler = new AudioHandler({
    warningNode: document.getElementById("audio-warning"),
    statsNode: audioStatsNode,
    audioOutput,
    audioFilterFreq: parsedQuery.audiofilterfreq,
    audioFilterQ: parsedQuery.audiofilterq,
    speakerAmount,
    audioLatencyMs: parsedQuery.audioLatencyMs,
    noSeek,
    cpuSpeed,
    isAtom: model.isAtom,
    hasMusic5000: settings.hasMusic5000,
});
// Firefox will report that audio is suspended even when it will
// start playing without user interaction, so we need to delay a
// little to get a reliable indication.
window.setTimeout(() => audioHandler.checkStatus(), 1000);

new QuickSettings(settings);

// ------------------------------------------------------------------------
// The machine, the loop that runs it, and everything that feeds it.
// ------------------------------------------------------------------------

// Depends on the settings having applied the URL parameters.
const machine = new Machine({
    model,
    settings,
    parsedQuery,
    keyLayout,
    cpuMultiplier,
    extraRoms,
    stationId,
    userPort: accessibilitySwitches.userPort,
    printer,
    speechOutput,
    video,
    audioHandler,
    dbgr,
});
const processor = machine.processor;

const { keyboard } = new KeyboardSetup({
    actions: {
        toggleDebugger: () => {
            if (!dbgr.enabled()) return loop.stop(true);
            dbgr.hide();
            loop.go();
        },
        toggleFast: () => loop.toggleFastAsPossible(),
        openRewind: () => rewindUI.open(),
        openPrinter: () => frontPanel.checkPrinterWindow(),
        openMedia: (target) => mediaWindow.openFor(target),
        pause: () => loop.stop(false),
        resume: () => {
            // However the machine was stopped, coming back leaves no debugger behind it.
            dbgr.hide();
            loop.go();
        },
        paste: (text) => keyboard.sendRawKeyboard(autoBoot.stringToMachineKeys(text), true),
        onAnyKeyDown: () => {
            audioHandler.tryResume();
            inputs.ensureMicrophoneRunning();
        },
    },
    accessibilitySwitches,
    processor,
    dbgr,
    keyLayout,
});

const loop = new EmulationLoop({
    processor,
    display,
    audioHandler,
    dbgr,
    gamepad,
    keyboard,
    clocksPerSecond,
    cpuSpeed,
    fastTape: !!parsedQuery.fasttape,
    audioStatsNode,
});

const runControls = new RunControls({ loop, dbgr, keyboard });

const modals = new Modals({ loop });

const drives = new Drives({ fdc: processor.fdc, driveTracks, confirm: modals.confirm.bind(modals), urlState });
const media = new MediaLoader({
    processor,
    model,
    drives,
    urlState,
    modals,
    isSnapshotFile,
    loadSnapshot: (file, buffer) => snapshots.loadStateFromFile(file, buffer),
});
const autoBoot = new Autoboot({
    model,
    processor,
    sendKeys: (keysToSend, check) => keyboard.sendRawKeyboard(keysToSend, check),
});
new SthSource({ media });
new HfeSource({ media });
new BitshiftersSource({ media });
new GitHubSource({ media });
const googleDriveSource = new GoogleDriveSource({ media });
const machineSwitch = new MachineSwitch({ model, processor, urlState, modals });
const snapshots = new SnapshotUI({
    processor,
    model,
    video,
    media,
    urlState,
    modals,
    loop,
});

const inputs = new AnalogueInputs({
    processor,
    screenCanvas,
    getGamepads: machine.spec.getGamepads,
    settings,
    audioHandler,
});
if (settings.microphoneChannel !== undefined) {
    // We need to use setTimeout to make sure this runs after the page has loaded
    // This is needed because some browsers require user interaction for audio context
    setTimeout(async () => {
        await inputs.setupMicrophone();
    }, 1000);
}
// Apply ADC source settings from URL parameters
inputs.updateAdcSources(settings.mouseJoystickEnabled, settings.microphoneChannel);

const frontPanel = new FrontPanel({ processor, model, printer, loop });

// ------------------------------------------------------------------------
// Rewind, the visualiser and the layout.
// ------------------------------------------------------------------------

const rewindUI = new RewindUI({ processor, video, loop });
rewindUI.updateButtonState();

const discVisualiser = new DiscVisualiser({ fdc: processor.fdc });
const mediaWindow = new MediaWindow({
    media,
    drives,
    processor,
    model,
    loop,
    visualiser: discVisualiser,
    autoboot: (image) => autoBoot.boot(image),
    driveSource: googleDriveSource,
    machineSwitch,
});

const layout = new Layout({
    screenCanvas,
    display,
    embed: !!parsedQuery.embed,
    sidebars: { left: parsedQuery.sbLeft, right: parsedQuery.sbRight, bottom: parsedQuery.sbBottom },
});

// Everything a setting can reach now exists.
settings.on("audioOutput", (output) => audioHandler.setAudioOutput(output));
settings.on("speakerAmount", (amount) => audioHandler.setSpeakerAmount(amount));
settings.on("displayMode", (mode) => {
    display.setMode(mode);
    config.setPersistenceInUse(display.persistenceSetting());
    // The monitor picture may have changed shape.
    layout.resize();
});
config.setPersistenceInUse(display.persistenceSetting());
for (const { setting } of persistenceSettings()) {
    display.setPersistence(setting, settings[setting]);
    settings.on(setting, (persistence) => display.setPersistence(setting, persistence));
}
settings.on("keyLayout", (chosen) => keyboard.setKeyLayout(chosen));
settings.on("tubeCpuMultiplier", (multiplier) => {
    if (processor.hasTube) processor.tube.cpuMultiplier = multiplier;
});
const rerouteAnalogue = () => inputs.updateAdcSources(settings.mouseJoystickEnabled, settings.microphoneChannel);
settings.on("mouseJoystickEnabled", rerouteAnalogue);
settings.on("microphoneChannel", (channel) => {
    rerouteAnalogue();
    if (channel !== undefined) inputs.setupMicrophone();
});
config.addEventListener("restart-required", async () => {
    const restart = await modals.confirm(
        "Your change is saved, but only takes effect when the emulator restarts. Restart now?",
        "Restart now",
        "Later",
    );
    if (restart) window.location.reload();
});

const page = new PageActions({ loop, processor, keyboard, audioHandler, rewindUI, modals, parsedQuery, version });

// ------------------------------------------------------------------------
// Start it up.
// ------------------------------------------------------------------------

const basicNeedsRun = parsedQuery.loadBasic !== undefined && needsAutoboot === "run";
if (parsedQuery.loadBasic) needsAutoboot = "";

// The boot disc is described while the machine starts, so a link that boots a disc without
// naming a model can be switched to the machine the disc needs before the boot.
const bootDisc = needsAutoboot === "boot" && !parsedQuery.model ? media.describe(discImage) : null;
/** @returns {Promise<boolean>} whether the page is on its way to the machine its boot disc needs */
const switchForBootDisc = async (d) =>
    !!d &&
    !machineSwitch.satisfies(d) &&
    machineSwitch.switchFor(d, media.slots.drive(0), { boot: true, replace: true });
const startPromise = machine.start({
    media,
    autoBoot,
    discImage,
    discImageInUrl: !!queryDiscImage,
    secondDiscImage,
    tape: parsedQuery.tape,
    mmcImage,
    loadBasic: parsedQuery.loadBasic,
    embedBasic: parsedQuery.embedBasic,
    basicNeedsRun,
});

(async () => {
    try {
        machineSwitch.announce();
        await startPromise;
        if (await switchForBootDisc(await bootDisc)) return;

        switch (needsAutoboot) {
            case "boot":
                autoBoot.boot(discImage);
                break;
            case "type":
                autoBoot.type(autoType);
                break;
            case "chain":
                autoBoot.chainTape();
                break;
            case "run":
                autoBoot.runTape();
                break;
            default:
                break;
        }

        if (parsedQuery.patch) {
            dbgr.setPatch(parsedQuery.patch);
        }

        // Restore the state a cross-model reload stashed, if there is one.
        await snapshots.restorePendingState();

        loop.go();
        const rendezvous = createRendezvousClient();
        startSessionFromUrl(parsedQuery, { processor, model, loop, keyboard, urlState, version, rendezvous });
    } catch (error) {
        console.error("Error initialising emulator:", error);
        modals.showError("initialising", error);
    }
})();

// ------------------------------------------------------------------------
// The console surface the wiki documents, and the desktop app's hooks.
// ------------------------------------------------------------------------

exposeConsoleSurface(window, { loop, processor, video, audioHandler });

// Hooks for electron.
electron({
    settings,
    media,
    drives,
    modals,
    loadStateFile: snapshots.loadStateFromFile.bind(snapshots),
    actions: {
        "soft-reset": () => page.softReset(),
        "hard-reset": () => page.hardReset(),
        "save-state": () => snapshots.saveState(),
        media: () => mediaWindow.open(),
        rewind: () => rewindUI.open(),
        pause: () => runControls.pause(),
        resume: () => runControls.resume(),
    },
});
